import { useDroppable } from '@dnd-kit/core';
import type { ColunaCards, PrazoCard } from '../types';
import { PT_DIA, VAR_COR, fmtData } from '../utils';
import { CardPrazo } from './CardPrazo';
import { useMe } from '../hooks';

interface Props {
  coluna: ColunaCards;
  cor: 'vermelho' | 'azul_escuro' | 'verde' | 'cinza';
  hint: string;
  diasSemana: string[];      // ISO dates da semana
  onCardClick: (id: number) => void;
  /** Realça a coluna inteira (usado em 'Pronto p/ protocolo'). */
  destacar?: boolean;
  /** Quando um card está sendo arrastado E é compatível com esta coluna. */
  dropOk?: boolean;
  /** Quando o card que está sendo arrastado NÃO pode vir pra cá. */
  dropBloqueado?: boolean;
}

export function ColunaKanban({
  coluna, cor, hint, diasSemana, onCardClick, destacar,
  dropOk = false, dropBloqueado = false,
}: Props) {
  const { setNodeRef, isOver } = useDroppable({ id: `col-${coluna.coluna}` });
  const { data: me } = useMe();
  const isAdmin = me?.papel === 'admin';
  const isColab = me?.papel === 'colaborador';
  const isProntoOuConcluido = coluna.coluna === 'pronto_protocolo' || coluna.coluna === 'concluidos';
  const draggable = isAdmin || (isColab && isProntoOuConcluido);

  // Visual feedback durante drag:
  //   isOver + dropBloqueado  →  ring vermelho ("não posso")
  //   isOver + dropOk         →  ring verde forte + brilho
  //   dropOk                  →  destaque leve (atrai)
  //   default                 →  comportamento da Fase 4.5
  const draggingFeedback =
    isOver && dropBloqueado
      ? 'ring-2 ring-red-400 dark:ring-red-700 bg-red-50 dark:bg-red-900/20'
      : isOver && dropOk
      ? 'ring-4 ring-blue-500 dark:ring-blue-400 scale-[1.01] shadow-lg'
      : dropOk
      ? 'ring-1 ring-blue-200 dark:ring-blue-800'
      : '';

  const isTriagem = coluna.coluna === 'nao_feitos';

  const baseWrap =
    isTriagem
    ? 'bg-gradient-to-b from-red-500/5 to-red-500/10 dark:from-red-950/20 dark:to-red-950/10 rounded-xl border-2 border-red-400/80 dark:border-red-700/80 shadow-md shadow-red-500/5'
    : 'bg-slate-50/70 dark:bg-slate-900/40 border border-slate-250 dark:border-slate-800/80 rounded-xl shadow-sm hover:shadow-md transition-all duration-200';
  const wrapClasses = `${baseWrap} transition ${draggingFeedback}`;
  const headerClasses =
    isTriagem
    ? 'px-4 py-3 border-b border-red-200 dark:border-red-800/80 flex items-center justify-between bg-red-100/30 dark:bg-red-950/30 rounded-t-xl'
    : 'px-4 py-3 border-b border-slate-200 dark:border-slate-800/80 flex items-center justify-between';
  const tituloClasses =
    isTriagem
    ? 'font-display font-bold text-red-800 dark:text-red-200 uppercase tracking-wide text-sm'
    : 'font-display font-bold text-slate-700 dark:text-slate-200 uppercase tracking-wide text-sm';
  const badgeClasses =
    isTriagem
    ? 'px-2 py-0.5 bg-red-600 dark:bg-red-500 text-white rounded-md text-xs font-bold font-mono'
    : 'px-2 py-0.5 bg-slate-200/80 dark:bg-slate-800 text-slate-600 dark:text-slate-400 rounded-md text-xs font-bold font-mono';

  return (
    <section ref={setNodeRef} className={wrapClasses}>
      <header className={headerClasses}>
        <div className="flex items-center gap-2">
          {coluna.coluna !== 'nao_feitos' && (
              <span className="w-2 h-2 rounded-full" style={{ background: VAR_COR[cor] }} />
            )}
          <h2 className={tituloClasses}>
            {coluna.titulo}
          </h2>
          <span className="text-xs text-slate-400 dark:text-slate-500 font-medium">{hint}</span>
        </div>
        <div className="flex items-center gap-1.5 font-sans">
          <span className={badgeClasses}>
            {coluna.total}
          </span>
        </div>
      </header>
      <div className="col-scroll p-2 space-y-3">
        {diasSemana.map((dia) => {
          const cardsDia = coluna.cards.filter(
            (c: PrazoCard) =>
              (c.data_evento ?? c.data_vencimento) === dia,
          );
          const d = new Date(dia + 'T00:00:00');
          const ehFds = d.getDay() === 0 || d.getDay() === 6;
          return (
            <div key={dia} className="space-y-1">
              <div
                className={`dia-header px-2 py-1.5 text-[10px] font-bold uppercase tracking-wider rounded-md ${
                  ehFds
                    ? 'text-slate-400 dark:text-slate-650 bg-slate-100/50 dark:bg-slate-900/20'
                    : 'text-slate-500 dark:text-slate-400 bg-slate-100/70 dark:bg-slate-900/50 border border-slate-200/40 dark:border-slate-800/40'
                }`}
              >
                {PT_DIA[d.getDay()]}{' '}
                <span className="font-normal text-slate-400 dark:text-slate-500 font-sans ml-1">
                  {fmtData(dia, { curto: true })}
                </span>
              </div>
              {cardsDia.length > 0 ? (
                <div className="space-y-3 mt-1.5">
                  {cardsDia.map((c) => (
                    <CardPrazo
                      key={c.id_prazo}
                      card={c}
                      onClick={() => onCardClick(c.id_prazo)}
                      draggable={draggable}
                    />
                  ))}
                </div>
              ) : !ehFds ? (
                <p className="text-[11px] text-slate-400 dark:text-slate-500 italic px-2 py-1 mt-0.5">
                  — sem prazos —
                </p>
              ) : null}
            </div>
          );
        })}
      </div>
    </section>
  );
}
